import React, { useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Col, Row } from "reactstrap"
import {
  NeoModal,
  NeoButton,
  NeoInput,
  NeoSearch,
  NeoBadge,
  NeoConfirmDialog,
} from "../../../components/Common"
import Swal from 'sweetalert2'
import { useAttendanceModal } from "../hooks/useAttendanceModal"
import NotifyStudentsModal from "./NotifyStudentsModal"
import WaitlistPanel from "./WaitlistPanel"
import { usePlan } from "../../../hooks/usePlan"
import { useTenant } from "../../../hooks/useTenant"
import { FEATURE_KEYS } from "../../../constants/plans"

const memberStatusLabel = (s) => ({ active: 'Ativo', suspended: 'Suspenso', inactive: 'Inativo' }[s] || String(s))
const memberStatusColor = (s) => ({ active: 'success', suspended: 'warning' }[s] || 'secondary')
const isExperimental = (m) => ['experimental', 'single-session', 'trial'].includes(String(m.enrollmentType || m.type).toLowerCase())

const AttendanceModal = ({ isOpen, onClose, schedule, onAttendanceSaved }) => {
  const [showNotifyModal, setShowNotifyModal] = useState(false)
  const [showWaitlistPanel, setShowWaitlistPanel] = useState(false)
  const { hasFeature } = usePlan()
  const { idTenant } = useTenant()
  const canNotify = hasFeature(FEATURE_KEYS.AUTOMATIONS)
  const {
    members,
    loadingMembers,
    searchText,
    searchResults,
    justAddedId,
    attendanceStats,
    isDirty,
    showConfirm,
    title,
    setSearchText,
    setShowConfirm,
    isLoading,
    handleSelectSearchMember,
    handleMarkAbsence,
    handleMarkPresent,
    handleChangeJustification,
    handleSave,
    handleClose,
    handleForceClose,
    handleGoToProfile,
    isChangingTeacher,
    setIsChangingTeacher,
    substituteTeacher,
    setSubstituteTeacher,
    availableTeachers,
    isLoadingTeachers,
    isCanceling,
    handleCancelSession,
  } = useAttendanceModal({ isOpen, onClose, schedule, onAttendanceSaved })

  const footerContent = (
    <div className="d-flex w-100 justify-content-between align-items-center">
      <div className="d-flex flex-wrap gap-2">
        <NeoButton
          variant="outline-danger"
          icon="mdi-cancel"
          loading={isCanceling}
          loadingText="Cancelando..."
          disabled={isLoading('save') || schedule?.status === 'CANCELED'}
          onClick={async () => {
            const { value: cancelReason, isConfirmed } = await Swal.fire({
              title: "Cancelar Aula",
              text: "Selecione o motivo do cancelamento:",
              icon: "warning",
              input: 'select',
              inputOptions: {
                'admin': 'Administrativo (Ex: Feriado, Sem Luz) - Não penaliza',
                'teacher_absence': 'Falta do Professor - Penaliza Score'
              },
              inputPlaceholder: 'Selecione um motivo...',
              showCancelButton: true,
              confirmButtonColor: '#dc3545',
              cancelButtonColor: '#6c757d',
              confirmButtonText: 'Cancelar Aula',
              cancelButtonText: 'Fechar',
              inputValidator: (value) => new Promise((resolve) => {
                if (value === 'admin' || value === 'teacher_absence') resolve()
                else resolve('Você precisa selecionar um motivo válido!')
              })
            })
            if (isConfirmed && cancelReason) handleCancelSession(cancelReason)
          }}
        >
          <span className="d-none d-sm-inline">Cancelar Aula</span>
        </NeoButton>

        {canNotify && (
          <NeoButton
            variant="outline-success"
            icon="mdi-whatsapp"
            onClick={() => setShowNotifyModal(true)}
          >
            <span className="d-none d-sm-inline">Notificar Alunos</span>
          </NeoButton>
        )}
      </div>

      <NeoButton
        variant="primary"
        icon="mdi-content-save-outline"
        loading={isLoading('save')}
        loadingText="Salvando..."
        disabled={!isDirty && !substituteTeacher && attendanceStats.total > 0}
        onClick={handleSave}
      >
        <span className="d-none d-sm-inline">Salvar</span>
      </NeoButton>
    </div>
  )

  return (
    <React.Fragment>
      <NeoModal
        isOpen={isOpen}
        toggle={handleClose}
        title="Controle de Presença"
        subtitle={title}
        headerIcon="mdi-account-check-outline"
        size="xl"
        scrollable={false}
        bodyClassName="p-0"
        bodyStyle={{ maxHeight: "78vh", overflow: "hidden" }}
        backdrop="static"
        footerActions={footerContent}
      >
        <div className="d-flex h-100">
          {/* Main content */}
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto", maxHeight: "78vh" }}>

            {/* Class Info Summary */}
            <div className="attendance-summary-card mb-3">
              <Row className="align-items-center g-3">
                <Col lg={7}>
                  <div className="d-flex flex-wrap gap-4 align-items-center">
                    <div className="attendance-info-row">
                      <div className="attendance-icon-box"><i className="mdi mdi-account-star"></i></div>
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="attendance-label">Professor</span>
                          {!isChangingTeacher && (
                            <NeoButton
                              variant="soft-primary"
                              size="sm"
                              circle
                              icon="mdi-swap-horizontal"
                              title="Trocar Titular / Registrar Substituto"
                              onClick={() => setIsChangingTeacher(true)}
                            />
                          )}
                        </div>
                        {!isChangingTeacher ? (
                          <div className="fw-bold text-dark fs-6 lh-1 mt-1 d-flex align-items-center gap-2">
                            {substituteTeacher ? (substituteTeacher.fullName || substituteTeacher.name) : schedule?.employeeName || "Não definido"}
                            {substituteTeacher && <NeoBadge color="warning" pill>Substituto</NeoBadge>}
                          </div>
                        ) : (
                          <div className="mt-1 d-flex align-items-center gap-1" style={{ minWidth: "180px" }}>
                            {isLoadingTeachers ? (
                              <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                            ) : (
                              <>
                                <NeoInput
                                  type="select"
                                  size="sm"
                                  value={substituteTeacher?.id || ""}
                                  onChange={e => {
                                    const prof = availableTeachers.find(a => a.id === e.target.value)
                                    setSubstituteTeacher(prof || null)
                                    setIsChangingTeacher(false)
                                  }}
                                  options={[
                                    { value: "", label: "Selecione..." },
                                    ...availableTeachers.map(t => ({ value: t.id, label: t.fullName || t.name }))
                                  ]}
                                />
                                <NeoButton
                                  variant="soft-secondary"
                                  size="sm"
                                  circle
                                  icon="mdi-close"
                                  onClick={() => setIsChangingTeacher(false)}
                                />
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="attendance-info-row">
                      <div className="attendance-icon-box"><i className="mdi mdi-clock-outline"></i></div>
                      <div>
                        <span className="attendance-label">Horário</span>
                        <div className="fw-bold text-dark fs-6 lh-1 mt-1">{schedule?.startTime} - {schedule?.endTime}</div>
                      </div>
                    </div>

                    <div className="attendance-info-row d-none d-md-flex">
                      <div className="attendance-icon-box"><i className="mdi mdi-map-marker-radius-outline"></i></div>
                      <div>
                        <span className="attendance-label">Local</span>
                        <div className="fw-bold text-dark fs-6 lh-1 mt-1">{schedule?.areaName || "Geral"}</div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={5}>
                  <div className="d-flex gap-2 justify-content-lg-end justify-content-start flex-wrap">
                    <div className="attendance-stat-box">
                      <div className="d-flex flex-column text-center w-100">
                        <span className="attendance-label">Presentes</span>
                        <span className="text-success fw-bold fs-5 lh-1 mt-1">{attendanceStats.present}</span>
                      </div>
                    </div>
                    <div className="attendance-stat-box">
                      <div className="d-flex flex-column text-center w-100">
                        <span className="attendance-label">Ausentes</span>
                        <span className="text-danger fw-bold fs-5 lh-1 mt-1">{attendanceStats.absent}</span>
                      </div>
                    </div>
                    <div className="attendance-stat-box attendance-stat-box--dark">
                      <div className="d-flex flex-column text-center w-100">
                        <span className="attendance-label attendance-label--light">Total</span>
                        <span className="text-white fw-bold fs-5 lh-1 mt-1">{attendanceStats.total}</span>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Search + Waitlist */}
            <div className="d-flex gap-2 mb-4 align-items-center">
              <div className="position-relative flex-grow-1">
                <NeoSearch
                  placeholder="Buscar aluno para adicionar como reposição..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onClear={() => setSearchText("")}
                  disabled={isLoading('addExtra')}
                />
                {searchResults.length > 0 && (
                  <div className="attendance-search-dropdown">
                    <div className="attendance-search-dropdown__header">
                      <i className="mdi mdi-account-plus-outline me-1" />
                      {searchResults.length} aluno{searchResults.length > 1 ? 's' : ''} encontrado{searchResults.length > 1 ? 's' : ''}
                    </div>
                    {searchResults.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        className="attendance-search-dropdown__item"
                        onClick={() => handleSelectSearchMember(c)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          {c.photo ? (
                            <img src={c.photo} alt={c.name} className="attendance-search-avatar" />
                          ) : (
                            <div className="attendance-search-avatar--placeholder">{c.name?.charAt(0)}</div>
                          )}
                          <div>
                            <div className="fw-bold text-dark font-size-13">{c.name}</div>
                            <div className="text-muted font-size-11">
                              {c.memberNumber ? `Matrícula #${c.memberNumber}` : 'Sem Matrícula'}
                            </div>
                          </div>
                        </div>
                        <i className="mdi mdi-plus-circle text-primary fs-5 opacity-75" />
                      </button>
                    ))}
                  </div>
                )}
                {searchText.length >= 2 && searchResults.length === 0 && !isLoading('addExtra') && (
                  <div className="attendance-search-empty">
                    <i className="mdi mdi-account-search text-muted fs-3 d-block mb-1" />
                    <small className="text-muted">Nenhum aluno encontrado para "{searchText}"</small>
                  </div>
                )}
              </div>

              {Number(schedule?.waitlistCount || 0) > 0 && (
                <NeoButton
                  variant={showWaitlistPanel ? "warning" : "outline-chocolate"}
                  icon="mdi-account-clock-outline"
                  onClick={() => setShowWaitlistPanel(!showWaitlistPanel)}
                >
                  <span className="d-none d-md-inline">Espera</span>
                  <NeoBadge color={showWaitlistPanel ? "light" : "warning"} className="ms-1">
                    {schedule.waitlistCount}
                  </NeoBadge>
                </NeoButton>
              )}
            </div>

            <Row className="g-4">
              {/* Presentes */}
              <Col md={12}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="section-title mb-0">
                    <i className="mdi mdi-check-circle-outline text-success me-2"></i>
                    Lista de Presença
                  </h6>
                  <NeoBadge color="success">{attendanceStats.present} Alunos</NeoBadge>
                </div>

                <div className="attendance-list-container">
                  {loadingMembers ? (
                    <div className="text-center py-4 bg-light rounded-3 border border-dashed">
                      <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                      <p className="text-muted mb-0 mt-2">Carregando alunos matriculados...</p>
                    </div>
                  ) : members.filter(c => c.status !== "absent").length === 0 ? (
                    <div className="text-center py-4 bg-light rounded-3 border border-dashed">
                      <p className="text-muted mb-0">Nenhum aluno marcado como presente.</p>
                    </div>
                  ) : null}

                  <div className="d-flex flex-column gap-2">
                    {members.filter(c => c.status !== "absent").map(member => (
                      <div
                        key={member.id}
                        className={classNames("attendance-item", { "attendance-item--new": String(member.id) === String(justAddedId) })}
                      >
                        <div className="attendance-item__avatar-wrap">
                          {member.photo
                            ? <img src={member.photo} alt={member.name} className="attendance-item__avatar" />
                            : <div className="attendance-item__avatar-placeholder"><i className="mdi mdi-account"></i></div>
                          }
                          {isExperimental(member) && <span className="attendance-item__ex-badge" title="Experimental">EX</span>}
                        </div>

                        <div className="attendance-item__content">
                          <div className="fw-bold text-dark fs-5">{member.name}</div>
                          <div className="d-flex gap-2 align-items-center mt-1 flex-wrap">
                            {member.memberNumber && <span className="text-muted small fw-bold">#{member.memberNumber}</span>}
                            {member.memberStatus && <NeoBadge color={memberStatusColor(member.memberStatus)}>{memberStatusLabel(member.memberStatus)}</NeoBadge>}
                            {isExperimental(member) && <NeoBadge color="info">Experimental</NeoBadge>}
                          </div>
                        </div>

                        <div className="attendance-item__actions" onClick={e => e.stopPropagation()}>
                          <NeoButton variant="soft-primary" size="sm" square icon="mdi-eye-outline" onClick={() => handleGoToProfile(member.id)} title="Ver Perfil" />
                          <NeoButton variant="outline-danger" size="sm" icon="mdi-account-minus-outline" onClick={() => handleMarkAbsence(member.id)} title="Faltou">
                            <span className="d-none d-sm-inline">Faltou</span>
                          </NeoButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>

              {/* Ausentes */}
              <Col md={12}>
                <div className="d-flex align-items-center justify-content-between mt-2 mb-3">
                  <h6 className="section-title mb-0">
                    <i className="mdi mdi-close-circle-outline text-danger me-2"></i>
                    Ausentes
                  </h6>
                  <NeoBadge color="danger">{attendanceStats.absent} Alunos</NeoBadge>
                </div>

                <div className="attendance-list-container">
                  {attendanceStats.absent === 0 && (
                    <div className="text-center py-4 bg-light rounded-1 border border-dashed opacity-50">
                      <p className="text-muted mb-0">Nenhum aluno marcado como ausente.</p>
                    </div>
                  )}

                  <div className="d-flex flex-column gap-2">
                    {members.filter(c => c.status === "absent").map(member => (
                      <div key={member.id} className="attendance-item attendance-item--absent">
                        <div className="attendance-item__avatar-wrap attendance-item__avatar-wrap--faded">
                          {member.photo
                            ? <img src={member.photo} alt={member.name} className="attendance-item__avatar attendance-item__avatar--grayscale" />
                            : <div className="attendance-item__avatar-placeholder"><i className="mdi mdi-account"></i></div>
                          }
                          {isExperimental(member) && <span className="attendance-item__ex-badge attendance-item__ex-badge--grayscale" title="Experimental">EX</span>}
                        </div>

                        <div className="attendance-item__content">
                          <div className="fw-bold text-decoration-line-through text-muted fs-5">{member.name}</div>
                          <div className="d-flex gap-2 align-items-center mt-1 flex-wrap">
                            {member.memberNumber && <span className="text-muted small fw-bold">#{member.memberNumber}</span>}
                            {member.memberStatus && <NeoBadge color={memberStatusColor(member.memberStatus)}>{memberStatusLabel(member.memberStatus)}</NeoBadge>}
                            {isExperimental(member) && <NeoBadge color="info">Experimental</NeoBadge>}
                          </div>
                          <div className="mt-2">
                            <NeoInput
                              type="text"
                              placeholder="Adicionar motivo da falta (opcional)..."
                              value={member.justification || ''}
                              onChange={e => handleChangeJustification(member.id, e.target.value)}
                              size="sm"
                            />
                          </div>
                        </div>

                        <div className="attendance-item__actions" onClick={e => e.stopPropagation()}>
                          <NeoButton variant="soft-primary" size="sm" square icon="mdi-eye-outline" onClick={() => handleGoToProfile(member.id)} title="Ver Perfil" />
                          <NeoButton variant="success" size="sm" icon="mdi-account-plus" onClick={() => handleMarkPresent(member.id)} title="Presente">
                            <span className="d-none d-sm-inline">Presente</span>
                          </NeoButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Waitlist Sidebar */}
          <WaitlistPanel
            isOpen={showWaitlistPanel}
            onClose={() => setShowWaitlistPanel(false)}
            schedule={schedule}
          />
        </div>
      </NeoModal>

      <NeoConfirmDialog
        isOpen={showConfirm}
        toggle={() => setShowConfirm(false)}
        title="Atenção!"
        message="Você realizou alterações na chamada que ainda não foram salvas. Ao sair agora, todas as alterações serão perdidas."
        icon="mdi-alert-outline"
        iconVariant="warning"
        confirmText="Sair sem Salvar"
        cancelText="Voltar"
        onConfirm={handleForceClose}
        onCancel={() => setShowConfirm(false)}
      />

      <NotifyStudentsModal
        isOpen={showNotifyModal}
        toggle={() => setShowNotifyModal(false)}
        members={members}
        idTenant={idTenant}
      />
    </React.Fragment>
  )
}

AttendanceModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  schedule: PropTypes.shape({
    activityName: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    maxCapacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeName: PropTypes.string,
    areaName: PropTypes.string,
  }),
}

export default AttendanceModal
